import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    CheckCircle,
    Clock,
    AlertCircle,
    Bell,
    Search,
    Calendar,
    Target,
    TrendingUp,
    FileText,
    Users,
    Settings,
    MessageSquare,
    BarChart3,
    Star,
    Trophy,
    Award,
    Menu,
    X,
    LogOut,
    ChevronRight, TrendingDown
} from "lucide-react";
import { getCurrentUser, logoutUser } from "../../utils/auth";
import dashboardImg from "../../imgs/dashboard.svg";

const TeamMemberDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const user = getCurrentUser();
    const navigate = useNavigate();

    const handleLogout = () => {
        logoutUser();
        navigate("/login");
    };

    const menuItems = [
        { path: "/team-member/view-projects", label: "View Projects", icon: <FileText className="w-5 h-5" /> },
        { path: "/team-member/view-teams", label: "View Teams", icon: <Users className="w-5 h-5" /> },
        { path: "/team-member/settings", label: "Settings", icon: <Settings className="w-5 h-5" /> },
    ];

    const stats = [
        {
            label: "Assigned Tasks",
            value: "8",
            change: "+2",
            trend: "up",
            icon: <Target className="w-6 h-6 text-blue-600" />,
            color: "from-blue-500 to-blue-600"
        },
        {
            label: "Completed Today",
            value: "3",
            change: "+1",
            trend: "up",
            icon: <CheckCircle className="w-6 h-6 text-green-600" />,
            color: "from-green-500 to-green-600"
        },
        {
            label: "Pending Review",
            value: "2",
            change: "0",
            trend: "neutral",
            icon: <Clock className="w-6 h-6 text-yellow-600" />,
            color: "from-yellow-500 to-yellow-600"
        },
        {
            label: "Performance Score",
            value: "4.8",
            change: "+0.2",
            trend: "up",
            icon: <Star className="w-6 h-6 text-purple-600" />,
            color: "from-purple-500 to-purple-600"
        },
    ];

    const todaysTasks = [
        { title: "Design homepage layout", project: "Website Redesign", priority: "high", estimated: "3h", status: "in-progress" },
        { title: "Review API documentation", project: "Mobile App V2", priority: "medium", estimated: "1h", status: "pending" },
        { title: "Team sync meeting", project: "All Projects", priority: "low", estimated: "1h", status: "upcoming" },
        { title: "Bug fixes - Dashboard", project: "Analytics Platform", priority: "high", estimated: "2h", status: "completed" },
    ];

    const recentActivities = [
        { action: "Completed bug fixes", project: "Analytics Platform", time: "2 hours ago", points: 10 },
        { action: "Submitted design review", project: "Website Redesign", time: "4 hours ago", points: 5 },
        { action: "Attended team meeting", project: "All Projects", time: "1 day ago", points: 3 },
        { action: "Updated documentation", project: "Mobile App V2", time: "2 days ago", points: 7 },
    ];

    const teamUpdates = [
        { member: "Alex Chen", update: "Shared new design assets", time: "1 hour ago" },
        { member: "Maria Rodriguez", update: "Updated project timeline", time: "3 hours ago" },
        { member: "David Kim", update: "Fixed critical API issue", time: "5 hours ago" },
    ];

    const achievements = [
        { title: "Fast Learner", description: "Completed onboarding in record time", icon: <Trophy className="w-5 h-5" />, color: "from-yellow-500 to-yellow-600" },
        { title: "Team Player", description: "Helped 5 team members this week", icon: <Users className="w-5 h-5" />, color: "from-blue-500 to-blue-600" },
        { title: "Quality Star", description: "Zero bugs in last 10 tasks", icon: <Award className="w-5 h-5" />, color: "from-green-500 to-green-600" },
    ];

    const getPriorityColor = (priority) => {
        switch (priority) {
            case "high": return "bg-red-100 text-red-800";
            case "medium": return "bg-yellow-100 text-yellow-800";
            case "low": return "bg-green-100 text-green-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "completed": return "bg-green-100 text-green-800";
            case "in-progress": return "bg-blue-100 text-blue-800";
            case "pending": return "bg-yellow-100 text-yellow-800";
            case "upcoming": return "bg-gray-100 text-gray-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar */}
            <motion.aside
                initial={{ x: sidebarOpen ? 0 : -256 }}
                animate={{ x: sidebarOpen ? 0 : -256 }}
                className="fixed lg:relative h-full bg-white border-r border-gray-200 z-40 transition-all duration-300"
            >
                <div className="h-full flex flex-col w-64">
                    {/* Logo */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center">
                                    <Target className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-gray-900">TeamHub</h1>
                                    <p className="text-xs text-gray-500">Team Member</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4">
                        <div className="space-y-1">
                            {menuItems.map((item, index) => (
                                <motion.div
                                    key={index}
                                    whileHover={{ x: 4 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                >
                                    <Link
                                        to={item.path}
                                        className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:text-green-600 hover:bg-green-50 transition-colors group"
                                    >
                                        <div className="text-gray-400 group-hover:text-green-600">
                                            {item.icon}
                                        </div>
                                        <span className="font-medium">{item.label}</span>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>

                        {/* Quick Actions */}
                        <div className="mt-8">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 mb-3">
                                Quick Actions
                            </h3>
                            <div className="space-y-1">
                                <button className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:text-green-600 hover:bg-green-50 transition-colors w-full text-left">
                                    <MessageSquare className="w-5 h-5" />
                                    <span className="font-medium">Messages</span>
                                </button>
                                <button className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:text-green-600 hover:bg-green-50 transition-colors w-full text-left">
                                    <BarChart3 className="w-5 h-5" />
                                    <span className="font-medium">My Performance</span>
                                </button>
                            </div>
                        </div>
                    </nav>

                    {/* User Profile */}
                    <div className="p-4 border-t border-gray-200">
                        <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="relative">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {user?.email?.charAt(0).toUpperCase() || 'T'}
                  </span>
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {user?.email || 'Team Member'}
                                </p>
                                <p className="text-xs text-gray-500">Active Member</p>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Logout"
                            >
                                <LogOut className="w-4 h-4 text-gray-400 hover:text-red-500" />
                            </button>
                        </div>
                    </div>
                </div>
            </motion.aside>

            {/* Overlay for mobile */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 lg:hidden z-30"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <main className="flex-1 overflow-auto">
                {/* Header */}
                <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
                    <div className="px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={() => setSidebarOpen(true)}
                                    className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
                                >
                                    <Menu className="w-5 h-5" />
                                </button>
                                <div className="relative flex-1 max-w-md">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search tasks, projects, or updates..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 focus:outline-none"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <button className="relative p-2 hover:bg-gray-100 rounded-lg">
                                    <Bell className="w-5 h-5 text-gray-600" />
                                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                                </button>
                                <button className="p-2 hover:bg-gray-100 rounded-lg">
                                    <Calendar className="w-5 h-5 text-gray-600" />
                                </button>
                                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg" />
                            </div>
                        </div>
                    </div>
                </header>

                {/* Dashboard Content */}
                <div className="p-6">
                    {/* Welcome Banner */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                    Welcome back, {user?.email?.split('@')[0] || 'Team Member'}!
                                </h1>
                                <p className="text-gray-600">
                                    Here's your daily overview and what needs your attention.
                                </p>
                            </div>
                            <button className="btn-primary">
                                <CheckCircle className="w-5 h-5 mr-2" />
                                Mark as Ready
                            </button>
                        </div>
                    </motion.div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="card hover:shadow-medium"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} bg-opacity-10`}>
                                        {stat.icon}
                                    </div>
                                    {stat.trend !== "neutral" && (
                                        <div className={`flex items-center text-sm font-medium ${
                                            stat.trend === "up" ? "text-green-600" : "text-red-600"
                                        }`}>
                                            {stat.trend === "up" ? (
                                                <TrendingUp className="w-4 h-4 mr-1" />
                                            ) : (
                                                <TrendingDown className="w-4 h-4 mr-1" />
                                            )}
                                            {stat.change}
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                                <p className="text-sm text-gray-600">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6 mb-8">
                        {/* Today's Tasks */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="lg:col-span-2"
                        >
                            <div className="card">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-bold text-gray-900">Today's Tasks</h2>
                                    <button className="text-green-600 hover:text-green-700 text-sm font-medium">
                                        View all tasks
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {todaysTasks.map((task, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="p-4 border border-gray-200 rounded-xl hover:border-green-300 transition-colors"
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center space-x-3">
                                                    <div className={`w-3 h-3 rounded-full ${
                                                        task.status === "completed" ? "bg-green-500" :
                                                            task.status === "in-progress" ? "bg-blue-500" :
                                                                "bg-gray-300"
                                                    }`} />
                                                    <h3 className="font-semibold text-gray-900">{task.title}</h3>
                                                </div>
                                                <div className="flex items-center space-x-2">
                          <span className={`badge ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                                                    <span className={`badge ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <div className="flex items-center space-x-4">
                                                    <span className="text-gray-600">{task.project}</span>
                                                    <span className="text-gray-500">Est: {task.estimated}</span>
                                                </div>
                                                <button className="flex items-center text-green-600 hover:text-green-700 font-medium">
                                                    Start task
                                                    <ChevronRight className="w-4 h-4 ml-1" />
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* Recent Activities */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className="card h-full">
                                <h2 className="text-xl font-bold text-gray-900 mb-6">Recent Activities</h2>
                                <div className="space-y-4">
                                    {recentActivities.map((activity, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="p-3 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <p className="font-medium text-gray-900">{activity.action}</p>
                                                    <p className="text-sm text-gray-600">{activity.project}</p>
                                                </div>
                                                <span className="flex items-center text-sm font-medium text-green-600">
                          +{activity.points}
                                                    <Star className="w-4 h-4 ml-1" />
                        </span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm text-gray-500">
                                                <span>{activity.time}</span>
                                                <span className="text-green-600">Completed</span>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Team Updates */}
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Updates</h3>
                                    <div className="space-y-3">
                                        {teamUpdates.map((update, index) => (
                                            <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                                                <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-700 text-sm font-semibold">
                            {update.member.split(' ').map(n => n[0]).join('')}
                          </span>
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-900">{update.member}</p>
                                                    <p className="text-sm text-gray-600">{update.update}</p>
                                                </div>
                                                <span className="text-xs text-gray-500">{update.time}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Achievements & Dashboard Image */}
                    <div className="grid lg:grid-cols-2 gap-6">
                        {/* Achievements */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <div className="card h-full">
                                <h2 className="text-xl font-bold text-gray-900 mb-6">Your Achievements</h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {achievements.map((achievement, index) => (
                                        <motion.div
                                            key={index}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className="text-center p-4 rounded-xl border border-gray-200 hover:border-green-300 transition-colors"
                                        >
                                            <div className={`w-12 h-12 bg-gradient-to-br ${achievement.color} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                                                <div className="text-white">
                                                    {achievement.icon}
                                                </div>
                                            </div>
                                            <h3 className="font-semibold text-gray-900 mb-1">{achievement.title}</h3>
                                            <p className="text-sm text-gray-600">{achievement.description}</p>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Performance Summary */}
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-gray-900">This Week's Performance</h3>
                                        <span className="text-2xl font-bold text-green-600">4.8/5</span>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">Task Completion</span>
                                            <span className="font-medium text-gray-900">92%</span>
                                        </div>
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div className="h-full bg-green-500 rounded-full" style={{ width: '92%' }} />
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">Quality Score</span>
                                            <span className="font-medium text-gray-900">95%</span>
                                        </div>
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div className="h-full bg-green-500 rounded-full" style={{ width: '95%' }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Dashboard Image & Quick Actions */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <div className="card h-full">
                                <div className="text-center mb-6">
                                    <img
                                        src={dashboardImg}
                                        alt="Team Dashboard"
                                        className="w-full max-w-xs mx-auto mb-4 rounded-lg"
                                    />
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Your Workspace
                                    </h3>
                                    <p className="text-gray-600 text-sm">
                                        Stay productive and connected with your team. Access your projects, track progress, and collaborate efficiently.
                                    </p>
                                </div>

                                {/* Quick Actions */}
                                <div className="space-y-3 mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        {menuItems.map((item, index) => (
                                            <motion.div
                                                key={index}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                <Link
                                                    to={item.path}
                                                    className="flex items-center justify-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-green-50 hover:text-green-700 transition-colors"
                                                >
                                                    <div className="text-gray-600">
                                                        {item.icon}
                                                    </div>
                                                    <span className="font-medium text-sm">{item.label}</span>
                                                </Link>
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>

                                {/* Status Indicators */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <AlertCircle className="w-5 h-5 text-blue-600" />
                                            <span className="font-medium text-gray-700">Availability</span>
                                        </div>
                                        <span className="badge-success">Active</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <Clock className="w-5 h-5 text-yellow-600" />
                                            <span className="font-medium text-gray-700">Focus Time</span>
                                        </div>
                                        <span className="font-medium text-gray-900">3h 45m</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <MessageSquare className="w-5 h-5 text-purple-600" />
                                            <span className="font-medium text-gray-700">Unread Messages</span>
                                        </div>
                                        <span className="badge-info">3</span>
                                    </div>
                                </div>

                                <button className="w-full mt-6 btn-primary">
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Update Daily Status
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default TeamMemberDashboard;